/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { Profile } from "types/Profile";
import { AdobeId } from "reducers/CurrentUserReducer";
import getValidAccessToken from "utilities/getValidAccessToken";
import { Reducer } from "react";
import { AppState } from "types/AppState";

interface User {
  adobe_id: string;
  first_name: string;
  display_name: string;
  images: Record<string, string>;
}

export interface CreateProfileRequest {
  adobeId: AdobeId;
  firstName: string;
  lastName: string;
}

interface BehanceProfileResponse {
  user: User;
}

const PROFILE_CACHE_KEY = "Profile";

export const behanceApi = createApi({
  reducerPath: "profiles",
  baseQuery: fetchBaseQuery({ baseUrl: process.env.BEHANCE_URL }),
  tagTypes: [PROFILE_CACHE_KEY],
  endpoints: (builder) => ({
    createProfile: builder.mutation<boolean, CreateProfileRequest>({
      queryFn: async (profile) => {
        const accessToken = await getValidAccessToken();
        if (!profile || !accessToken) {
          return { data: false };
        }
        const body = `first_name=${encodeURIComponent(
          profile.firstName
        )}&last_name=${encodeURIComponent(profile.lastName)}`;
        const response = await fetch(
          `${process.env.BEHANCE_URL}v2/profile/editor?api_key=${process.env.ADOBE_IO_API_KEY}`,
          {
            method: "POST",
            body,
            headers: {
              Authorization: `Bearer ${accessToken.token}`,
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );
        return { data: response.ok };
      },
      invalidatesTags: (result, error, arg) => {
        return [{ type: PROFILE_CACHE_KEY, id: arg.adobeId }];
      },
    }),
    getProfileByAdobeId: builder.query<Profile | null, AdobeId | undefined>({
      queryFn: async (adobeId, api, extraOptions, fetchWithBQ) => {
        if (!adobeId) {
          return { data: null };
        }
        const response = await fetchWithBQ(
          `v2/users/${adobeId}?api_key=${process.env.ADOBE_IO_API_KEY}`
        );
        if (!response.data) {
          return { data: null };
        }
        const data = response.data as BehanceProfileResponse;
        const result = {
          adobeId: adobeId,
          displayName: data.user.display_name,
          firstName: data.user.first_name,
          images: data.user.images,
        };
        return { data: result };
      },
      providesTags: (result) => [
        { type: PROFILE_CACHE_KEY, id: result?.adobeId },
      ],
    }),
  }),
});

export const { useGetProfileByAdobeIdQuery } = behanceApi;

// Only works if this profile has previously been fetched with `initiate` or `useGetProfileByAdobeIdQuery`
export const selectProfileByAdobeId = (
  state: BehanceApiState,
  adobeId: AdobeId
): Profile | null | undefined => {
  const { data: profile } = behanceApi.endpoints.getProfileByAdobeId.select(
    adobeId
  )(state as any);
  return profile;
};

export type BehanceState =
  typeof behanceApi.reducer extends Reducer<infer S, infer A>
    ? S | undefined
    : never;

export const initialBehanceApiState: BehanceState = {} as BehanceState;

export const behanceApiReducerPath = behanceApi.reducerPath;
export interface BehanceApiState {
  [behanceApi.reducerPath]: BehanceState;
}

export const selectBehanceState = (
  state: AppState
): BehanceApiState | undefined => {
  return { [behanceApi.reducerPath]: state[behanceApi.reducerPath] };
};
